import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloseoutFormFields from "../../CloseoutFormFields/CloseoutFormFields";
import { Button, LoadingSpinner } from "pepsico-ds";
import Backdrop from "@mui/material/Backdrop";
import ModalPopUp from "../../../shared/modalPopUp";
import { transformProjectDetails } from "../../../utils/transformProjectDetails";
import { validateField } from "../../../utils/validators";
import {
  closeoutFieldNameMapping,
  closeoutRequiredFields,
  documentclassification,
} from "../../../utils/fieldConstants";
import DocumentUploadInput from "../../DocumentUploadInput/DocumentUploadInput";
import { CloseOutActions } from "../../../redux/closeoutslice/closeoutSlice";
import { CloseOutRadioActions } from "../../../redux/closeoutslice/closeOutRadioSlice";
import UseUploadInChunks from "../../../shared/Hooks/FileUploadInChunks";
import { saveFiles, saveIntakeForm } from "../../../services/intakeFormService";
import { useCanSubmitCloseOutForm } from "../../../hooks/access/canSubmitCloseOutForm";
import AppToaster from "../../../shared/appToaster";
import urls from "../../../configmap/config.json";
type RITMPropTypes = {
  RITMNumber: string;
};
const RITMRequestView = ({ RITMNumber }: RITMPropTypes) => {
  const canSubmitCloseOutForm = useCanSubmitCloseOutForm();
  const { projectDetails } = useSelector((state: any) => state.project);
  const [isEditMode, setEditMode] = useState(false);
  const [formattedProjectId, setFormattedProjectId] = useState<any>("");
  const [localProjectData, setLocalProjectData] = useState<any>();
  const [closeoutFormData, setCloseoutFormData] = React.useState<{
    [key: string]: any;
  }>({
    consultingCompany: "",
    coreDeliverables: "",
    finalCost: "",
    rating: "",
    purchaseOrderNumber: "",
    phasesTwo: "",
  });
  const [closeOutvalidInputError, setcloseOutValidInputError] = useState<{
    [key: string]: string | null;
  }>({});
  const [closeoutErrors, setCloseoutErrors] = useState<{
    [key: string]: string | null;
  }>({});
  const [closeoutFocusedFields, setCloseoutFocusedFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [isSaveDisabled, setIsSaveDisabled] = useState<any>(true);
  const [Loading, setLoading] = React.useState(false);
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useDispatch();
  const { fileUploadInChuncks } = UseUploadInChunks();
  const {
    isUploading,
    isFilesAdded,
    files,
    fileClassificationSelection,
    uploadedFilesSuccess,
    uploadedFiles,
  } = useSelector((state: any) => state.closeOut);
  const { gpid } = useSelector((state: any) => state.auth);
  const isSelfRequest =
    Number(projectDetails?.data[0]?.snow_user_id) === Number(gpid);
  const allowAction = isSelfRequest || canSubmitCloseOutForm;
  useEffect(() => {
    const generateRandomProjectId = () => {
      const randomId = Math.floor(Math.random() * 1000);
      setFormattedProjectId(`CF${String(randomId).padStart(3, "0")}`);
    };

    // Generate the ID when the component mounts
    generateRandomProjectId();
  }, []);
  useEffect(() => {
    const hasValidData = validData(projectDetails.data[0]);
    if (hasValidData) {
      setEditMode(true);
    } else {
      setEditMode(false);
    }
  }, []);
  useEffect(() => {
    if (projectDetails) {
      var { projectData } = transformProjectDetails(projectDetails);
      setLocalProjectData(projectData);
    }
  }, [projectDetails]);

  useEffect(() => {
    if (localProjectData) {
      // You can add logic here to update closeoutFormData based on localProjectData
      setCloseoutFormData({
        consultingCompany: localProjectData?.preferred_vendor || "",
        coreDeliverables: localProjectData?.core_deliverables || "",
        finalCost: "",
        rating: "",
        purchaseOrderNumber: "",
        phasesTwo: "",
      });
    }
  }, [localProjectData]);

  useEffect(() => {
    const newErrors: { [key: string]: string | null } = {};
    closeoutRequiredFields.forEach((field) => {
      if (closeoutFocusedFields[field]) {
        // Only check if field has been focused
        const displayName = closeoutFieldNameMapping[field] || field; // Use mapping for display name
        newErrors[field] = closeoutFormData[field]
          ? null
          : `${displayName} is a mandatory field.`; // Customize message
      }
    });
    setCloseoutErrors(newErrors);
  }, [closeoutFormData, closeoutFocusedFields]);
  useEffect(() => {
    // Use a timer for debounce-like behavior
    const timer = setTimeout(() => {
      // If it's in edit mode, the button should always be enabled
      if (isEditMode) {
        const hasClassified = files.every((file: any) => {
          const condition1 =
            file.document_classification ===
            documentclassification.confidential;
          const condition2 =
            file.document_classification === documentclassification.restricted;
          const condition3 =
            file.document_classification === documentclassification.internal;
          const condition4 =
            fileClassificationSelection[file.name] &&
            (fileClassificationSelection[file.name] ===
              documentclassification.confidentialCatergory ||
              fileClassificationSelection[file.name] ===
                documentclassification.restrictedCategory ||
              fileClassificationSelection[file.name] ===
                documentclassification.internalCategory);

          return condition1 || condition2 || condition3 || condition4;
        });
        const newlyUploadedFiles = files.filter(
          (file: any) => !uploadedFilesSuccess.includes(file.name)
        );
        setIsSaveDisabled(
          !(isFilesAdded && hasClassified) || newlyUploadedFiles.length === 0
        );
      } else {
        // Check for empty fields, using fallback for undefined/null
        const hasEmptyFields = closeoutRequiredFields.some((field) => {
          const value = closeoutFormData[field] || ""; // Fallback to empty string if field is undefined or null
          return value.trim() === ""; // If the value is an empty string or only spaces
        });

        // Check if closeoutFiles has files
        // const hasNoFiles = !(Array.isArray(closeoutFilesRef.current) ? closeoutFilesRef.current.length : Array.from(closeoutFilesRef.current).length);
        const hasFiles = files.length > 0;

        const hasErrors = Object.values(closeOutvalidInputError).some(
          (error) => error !== null
        );

        // Ensure every file has a valid classification ('confidential' or 'restricted')
        const allFilesClassifiedCorrectly = files.every(
          (file: any) =>
            fileClassificationSelection[file.name] &&
            (fileClassificationSelection[file.name] === "confidential" ||
              fileClassificationSelection[file.name] === "restricted" ||
              fileClassificationSelection[file.name] === "internal")
        );
        // Disable the button if there are empty fields or no files
        setIsSaveDisabled(
          hasEmptyFields ||
            !hasFiles ||
            hasErrors ||
            !allFilesClassifiedCorrectly
        );
      }
    }, 200); // Delay the check by 200ms (this is the debounce)

    // Clean up timeout on dependency change
    return () => clearTimeout(timer);
  }, [closeoutFormData, isEditMode, files, fileClassificationSelection]);

  const validData = (firstItem: any) => {
    return (
      firstItem &&
      firstItem.consulting_company !== "N/A" &&
      firstItem.consulting_company !== null &&
      firstItem.final_total_cost !== "N/A" &&
      firstItem.final_total_cost !== null &&
      firstItem.project_core_deliverables !== "N/A" &&
      firstItem.project_core_deliverables !== null &&
      firstItem.rate_value_received !== "N/A" &&
      firstItem.rate_value_received !== null
    );
  };

  // Handle field changes with debouncing and error clearing
  const closeoutFormHandleChange = useCallback(
    (value: any, key: string) => {
      // Immediate field update

      setCloseoutFormData((prev: any) => ({
        ...prev,
        [key]: value !== undefined ? value : "",
      }));
      const error = validateField(key, value);
      setcloseOutValidInputError((prevErrors) => ({
        ...prevErrors,
        [key]: error,
      }));
      if (value) {
        setCloseoutErrors((prevErrors: any) => ({
          ...prevErrors,
          [key]: null, // Reset error if the field is valid (non-empty)
        }));
      }
    },
    [closeoutFormData]
  );
  const closeoutHandleFileChange = (newFiles: File[]) => {
    // setCloseoutFiles(newFiles);
  };
  const handleCloseCloseoutDialog = () => {
    dispatch(CloseOutRadioActions.setRITMRequest(false));
  };

  const closeoutHandleFocus = (key: string) => {
    setCloseoutFocusedFields((prev) => ({ ...prev, [key]: true }));
  };

  const closeoutHandleBlur = (key: string) => {
    setCloseoutFocusedFields((prev) => ({ ...prev, [key]: false }));

    const displayName = closeoutFieldNameMapping[key] || key;
    if (!closeoutFormData[key]) {
      setCloseoutErrors((prev) => ({
        ...prev,
        [key]: `${displayName} is a mandatory field.`,
      }));
    }
  };
  const handleCloseoutPrevious = () => {
    dispatch(CloseOutRadioActions.setRITMRequest(false));
  };
  const handleSubmitCloseOut = async () => {
    setOpenModal(false);
    dispatch(CloseOutActions.setIsuploading(true));
    dispatch(CloseOutActions.setDisplayProgress(true));
    setLoading(true);
    const filesToUpload = files.filter((file: any) => {
      return !uploadedFilesSuccess.includes(file.name) && file.size > urls.minFileSize;
    });
    if (filesToUpload.length === 0) {
      dispatch(
        CloseOutActions.setErrorMessage(
          "All selected files have already been uploaded."
        )
      );
      dispatch(CloseOutActions.setIsuploading(false));
      return;
    }
    let localConstructedFiles: any[] = [];
    for (const file of filesToUpload) {
      try {
        const classification = fileClassificationSelection[file.name];
        // Upload the file in chunks and store the constructed files
        await fileUploadInChuncks(file, localConstructedFiles, classification);
        // Update Redux state with the uploaded file's name
        dispatch(
          CloseOutActions.setUploadedFiles([...uploadedFiles, file.name])
        );
        dispatch(
          CloseOutActions.setUploadedfileSuccess([
            ...uploadedFilesSuccess,
            file.name,
          ])
        );
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
      }
    }
    // After all files are uploaded, dispatch the constructed files to the store
    dispatch(CloseOutActions.setConstructedFiles(localConstructedFiles));
    dispatch(CloseOutActions.setDisplayProgress(false));
    if (localConstructedFiles.length) {
      dispatch(CloseOutActions.setIsuploading(false));
      if (isEditMode) {
        handleSaveEditFiles(localConstructedFiles);
      } else {
        handleSaveRITMRequest(localConstructedFiles);
      }
    }
  };
  const handleSaveEditFiles = async (localConstructedFiles: any) => {
    if (isSaveDisabled) {
      return;
    }
    // Set loading to true to disable multiple saves
    setLoading(true);
    try {
      const finalData = {
        ...closeoutFormData,
        ...localProjectData,
        supportingDocuments: localConstructedFiles,
      };

      // Update combined data array

      const saveFilesPayload = [
        {
          data: { project_id: finalData.project_id || "N/A", user_id: gpid },
          attached_files: localConstructedFiles,
        },
      ];

      // Call the saveFiles API
      await saveFiles(saveFilesPayload);

      // After the process is complete
      dispatch(
        CloseOutRadioActions.setSnackBarMessage({
          message: "The engagement data form has been uploaded successfully.",
          severity: "success",
        })
      );
      dispatch(CloseOutActions.resetAll());
    } catch (error: any) {
      dispatch(
        CloseOutRadioActions.setSnackBarMessage({
          severity: "error",
          message: error,
        })
      );
    } finally {
      // Make sure to set loading to false in both cases (success or error)
      setLoading(false);
      dispatch(CloseOutRadioActions.setRITMRequest(false));
      dispatch(CloseOutActions.resetAll());
    }
  };
  const handleSaveRITMRequest = async (localConstructedFiles: any) => {
    if (isSaveDisabled) {
      return;
    }
    // Set loading to true to disable multiple saves
    setLoading(true);

    try {
      const finalData = {
        ...closeoutFormData,
        ...localProjectData,
        supportingDocuments: localConstructedFiles,
      };
      const apiBody = [
        {
          data: {
            is_new: false,
            RITM_number: finalData.RITM_number || "N/A",
            project_id: finalData.project_id || "N/A",
            project_code: "6028889",
            user_id: gpid,
            consulting_company: finalData.consultingCompany || "N/A",
            final_total_cost: finalData.finalCost || "N/A",
            project_core_deliverables: finalData.coreDeliverables || "N/A",
            rate_value_received: finalData.rating || "N/A",
            purchase_order_number: finalData.purchaseOrderNumber || "N/A",
            close_out_additional_phase: finalData.phasesTwo || "N/A",
            form_type: 2,
            updated_date: new Date().toISOString(),
          },
          attached_files: finalData.supportingDocuments || [],
        },
      ];
      // When not in edit mode, use saveIntakeForm API
      await saveIntakeForm(apiBody);
      dispatch(
        CloseOutRadioActions.setSnackBarMessage({
          message: "The engagement data form has been uploaded successfully.",
          severity: "success",
        })
      );
      dispatch(CloseOutActions.resetAll());
    } catch (error: any) {
      dispatch(
        CloseOutRadioActions.setSnackBarMessage({
          severity: "error",
          message: error,
        })
      );
    } finally {
      setLoading(false);
      dispatch(CloseOutRadioActions.setRITMRequest(false));
      dispatch(CloseOutActions.resetAll());
    }
  };
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div>
          <CloseoutFormFields
            inputValue={RITMNumber}
            formattedProjectId={formattedProjectId}
            formData={closeoutFormData}
            handleChange={closeoutFormHandleChange}
            inputError={closeOutvalidInputError}
            handleFileChange={closeoutHandleFileChange}
            handleClose={handleCloseCloseoutDialog}
            closeoutErrors={closeoutErrors}
            closeoutOnFocus={closeoutHandleFocus}
            closeoutOnBlur={closeoutHandleBlur}
            mode={isEditMode ? "edit" : "create"}
          >
            <DocumentUploadInput showUploadSection={allowAction} />
          </CloseoutFormFields>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            marginTop: "15px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              iconLeading="arrow_back_ios"
              size="medium"
              text="Back"
              variant="tertiary"
              onClick={handleCloseoutPrevious}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Button
              size="medium"
              text="Cancel"
              variant="secondary"
              onClick={handleCloseCloseoutDialog}
            />
            {allowAction && (
              <Button
                size="medium"
                text="Submit"
                variant="primary"
                disabled={Loading || isSaveDisabled}
                onClick={() => setOpenModal(true)}
              />
            )}
          </div>
        </div>
        <Backdrop
          open={Loading}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <LoadingSpinner
            size={{
              height: "60px",
              width: "60px",
            }}
          />
          <div className="$font-heading-h6-medium" style={{ color: "#fff" }}>
            {isUploading ? "Uploading the files..." : "Submitting the form..."}
          </div>
        </Backdrop>
        <AppToaster />
      </div>
      <ModalPopUp
        open={openModal}
        handleClose={() => setOpenModal(false)}
        handleOnSubmit={handleSubmitCloseOut}
      />
    </>
  );
};

export default RITMRequestView;
